// ============================================================================
// src/lib/pigBatchesHubApi.ts — the Pig Batches hub's four physical reads/writes
// ----------------------------------------------------------------------------
// PigBatchesView owns two sidecars the route data catalog does not: the Global
// ADG override stored in the `ppp-pig-global-adg-v1` app_store row, and the pig
// weigh-in projection the system ADG estimate is seeded from. Both were inline
// PostgREST chains in the component. Only the TRANSPORT moved here — every
// decision the component made about them stays in the component:
//
//   * `loadPigGlobalAdg` returns the BUILDER, not a resolved row. The mount
//     effect still owns the `.then(({data, error}) => …)` tail, the
//     `as Promise<void>` assertion the library's PromiseLike declaration forces,
//     the soft-fail `.catch`, and the identical
//     {manualValue: null, updatedAt: null, updatedBy: null} fallback + warning
//     notice both paths write.
//   * `persistPigGlobalAdg` likewise returns the builder. The save is optimistic
//     and fire-and-forget: no await, no refetch, no invalidation, and on error
//     no state revert and no notice — only a console.warn. Awaiting it here, or
//     throwing on `error`, would change all three.
//   * `loadPigForecastSessions` and `loadPigForecastEntries` are two SEPARATE
//     functions on purpose. The effect must abort on a sessions error BEFORE
//     the weigh_ins request is issued, and must issue no entries request at all
//     when the session list is empty. A combined fetcher cannot express either,
//     so it is not offered.
//
// The `ppp-pig-global-adg-v1` literal stays inline beside `.from('app_store')`
// in both functions rather than becoming a parameter. Passing it in would make
// `.eq('key', …)` dynamic, and tests/static/app_store_boundary_static.test.js
// counts KEYED reads by matching the literal within 120 characters of the
// table name — a parameter drops this module out of that observation entirely.
// ============================================================================

import {savePigGlobalAdgOnServer} from './appStoreConfigOperationApi.js';
import type {SupabaseClient} from './supabaseTypes.js';

// The ppp-pig-global-adg-v1 app_store row. null while loading; the load and the
// save path both write exactly these three fields.
export type PigGlobalAdgRow = {manualValue: number | string | null; updatedAt: string | null; updatedBy: string | null};

export function loadPigGlobalAdg(sb: SupabaseClient) {
  return sb.from('app_store').select('data').eq('key', 'ppp-pig-global-adg-v1').maybeSingle();
}

export function persistPigGlobalAdg(sb: SupabaseClient, next: PigGlobalAdgRow) {
  return savePigGlobalAdgOnServer(sb, next);
}

export function loadPigForecastSessions(sb: SupabaseClient) {
  return sb
    .from('weigh_in_sessions')
    .select('id, batch_id, date, status, started_at')
    .eq('species', 'pig')
    .order('date', {ascending: false});
}

// The parameter is named `ids` because the caller's variable is, and the two
// spellings have to agree: the B3.3 call-site inventory fingerprints a chain by
// its argument SHAPE, and an unresolvable argument is recorded as its own
// expression text. Renaming it would report this read as a new site rather than
// the moved one it is.
export function loadPigForecastEntries(sb: SupabaseClient, ids: string[]) {
  return sb.from('weigh_ins').select('session_id, weight').in('session_id', ids);
}
